<template>
  <div class="flex h-screen bg-neutral-50">
    <!-- Mobile Backdrop -->
    <div
      v-if="isMobileMenuOpen"
      class="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
      @click="closeMobileMenu"
    ></div>

    <!-- Sidebar -->
    <div
      @mouseenter="handleSidebarHover(true)"
      @mouseleave="handleSidebarHover(false)"
      :class="[
        'bg-white shadow-lg flex flex-col transition-all duration-300 ease-in-out',
        // Width logic:
        // - Mobile: full width when open (w-64)
        // - Desktop: collapsed shows icons only (w-16), expanded shows full width (w-64)
        isMobileMenuOpen ? 'w-64' : isExpanded ? 'w-64' : 'w-16',
        // Position: fixed for desktop to overlap content, mobile drawer
        'fixed inset-y-0 left-0',
        // Z-index: higher when expanded to overlap content
        isExpanded || isMobileMenuOpen ? 'z-50' : 'z-40',
        // Mobile: slide in/out based on menu state, Desktop: always visible
        isMobileMenuOpen
          ? 'translate-x-0'
          : '-translate-x-full md:translate-x-0',
      ]"
    >
      <!-- Community Header -->
      <div :class="isExpanded || isMobileMenuOpen ? 'p-4' : 'p-4'">
        <div v-if="isExpanded || isMobileMenuOpen" class="flex flex-col">
          <div class="flex items-center min-w-0">
            <NeibrPayLogo size="lg" />
          </div>
        </div>

        <div v-else class="flex flex-col items-center">
          <NeibrPayLogo icon-only size="md" :title="communityName" />
        </div>
      </div>

      <!-- Navigation Links -->
      <nav
        :class="[
          'flex-1 overflow-y-auto',
          isExpanded || isMobileMenuOpen ? 'py-6 px-4' : 'py-4 px-2',
        ]"
        @click="handleNavClick"
      >
        <ul class="space-y-1">
          <li>
            <router-link
              to="/"
              :class="getNavLinkClass('Dashboard')"
              :title="!(isExpanded || isMobileMenuOpen) ? 'Dashboard' : ''"
            >
              <svg
                :class="[
                  'w-5 h-5',
                  isExpanded || isMobileMenuOpen ? 'mr-3' : '',
                ]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                />
              </svg>
              <span v-if="isExpanded || isMobileMenuOpen">Dashboard</span>
            </router-link>
          </li>

          <li>
            <router-link
              to="/invoices"
              :class="getNavLinkClass('Invoices')"
              :title="!(isExpanded || isMobileMenuOpen) ? 'HOA Dues' : ''"
            >
              <svg
                :class="[
                  'w-5 h-5',
                  isExpanded || isMobileMenuOpen ? 'mr-3' : '',
                ]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <span v-if="isExpanded || isMobileMenuOpen">HOA Dues</span>
            </router-link>
          </li>

          <!-- Charges - Hidden for residents -->
          <li v-if="!isResident">
            <router-link
              to="/charges"
              :class="getNavLinkClass(['Charges', 'AddCharge', 'EditCharge'])"
              :title="!(isExpanded || isMobileMenuOpen) ? 'Charges' : ''"
            >
              <svg
                :class="[
                  'w-5 h-5',
                  isExpanded || isMobileMenuOpen ? 'mr-3' : '',
                ]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                />
              </svg>
              <span v-if="isExpanded || isMobileMenuOpen">Charges</span>
            </router-link>
          </li>

          <!-- Residents (People) - Hidden for residents -->
          <li v-if="!isResident">
            <router-link
              to="/people"
              :class="getNavLinkClass('People')"
              :title="!(isExpanded || isMobileMenuOpen) ? 'Residents' : ''"
            >
              <svg
                :class="[
                  'w-5 h-5',
                  isExpanded || isMobileMenuOpen ? 'mr-3' : '',
                ]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                />
              </svg>
              <span v-if="isExpanded || isMobileMenuOpen">Residents</span>
            </router-link>
          </li>

          <li>
            <router-link
              to="/units"
              :class="getNavLinkClass(['Units', 'AddUnit', 'EditUnit'])"
              :title="!(isExpanded || isMobileMenuOpen) ? 'Units' : ''"
            >
              <svg
                :class="[
                  'w-5 h-5',
                  isExpanded || isMobileMenuOpen ? 'mr-3' : '',
                ]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                />
              </svg>
              <span v-if="isExpanded || isMobileMenuOpen">Units</span>
            </router-link>
          </li>

          <li>
            <router-link
              to="/payments"
              :class="getNavLinkClass('Payments')"
              :title="!(isExpanded || isMobileMenuOpen) ? 'Payments' : ''"
            >
              <svg
                :class="[
                  'w-5 h-5',
                  isExpanded || isMobileMenuOpen ? 'mr-3' : '',
                ]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                />
              </svg>
              <span v-if="isExpanded || isMobileMenuOpen">Payments</span>
            </router-link>
          </li>

          <li>
            <router-link
              to="/expenses"
              :class="
                getNavLinkClass([
                  'Expenses',
                  'AddExpense',
                  'EditExpense',
                  'ExpenseDetail',
                ])
              "
              :title="!(isExpanded || isMobileMenuOpen) ? 'Expenses' : ''"
            >
              <svg
                :class="[
                  'w-5 h-5',
                  isExpanded || isMobileMenuOpen ? 'mr-3' : '',
                ]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
              <span v-if="isExpanded || isMobileMenuOpen">Expenses</span>
            </router-link>
          </li>

          <li>
            <router-link
              to="/budget"
              :class="getNavLinkClass('Budget')"
              :title="!(isExpanded || isMobileMenuOpen) ? 'Budget' : ''"
            >
              <svg
                :class="[
                  'w-5 h-5',
                  isExpanded || isMobileMenuOpen ? 'mr-3' : '',
                ]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
              <span v-if="isExpanded || isMobileMenuOpen">Budget</span>
            </router-link>
          </li>

          <!-- Vendors - Hidden for residents -->
          <li v-if="!isResident">
            <router-link
              to="/vendors"
              :class="getNavLinkClass('Vendors')"
              :title="!(isExpanded || isMobileMenuOpen) ? 'Vendors' : ''"
            >
              <svg
                :class="[
                  'w-5 h-5',
                  isExpanded || isMobileMenuOpen ? 'mr-3' : '',
                ]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                />
              </svg>
              <span v-if="isExpanded || isMobileMenuOpen">Vendors</span>
            </router-link>
          </li>

          <li>
            <router-link
              to="/documents"
              :class="getNavLinkClass('Documents')"
              :title="!(isExpanded || isMobileMenuOpen) ? 'Documents' : ''"
            >
              <svg
                :class="[
                  'w-5 h-5',
                  isExpanded || isMobileMenuOpen ? 'mr-3' : '',
                ]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                />
              </svg>
              <span v-if="isExpanded || isMobileMenuOpen">Documents</span>
            </router-link>
          </li>

          <!-- Transactions -->
          <li>
            <router-link
              to="/transactions"
              :class="getNavLinkClass('Transactions')"
              :title="!(isExpanded || isMobileMenuOpen) ? 'Transactions' : ''"
            >
              <svg
                :class="[
                  'w-5 h-5',
                  isExpanded || isMobileMenuOpen ? 'mr-3' : '',
                ]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
              <span v-if="isExpanded || isMobileMenuOpen">Transactions</span>
            </router-link>
          </li>

          <!-- Announcements - Admin only -->
          <li v-if="isAdmin">
            <router-link
              to="/announcements"
              :class="
                getNavLinkClass([
                  'Announcements',
                  'AddAnnouncement',
                  'EditAnnouncement',
                ])
              "
              :title="!(isExpanded || isMobileMenuOpen) ? 'Announcements' : ''"
            >
              <svg
                :class="[
                  'w-5 h-5',
                  isExpanded || isMobileMenuOpen ? 'mr-3' : '',
                ]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3.14a7.5 7.5 0 011.291 7.239M19 13a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              <span v-if="isExpanded || isMobileMenuOpen">Announcements</span>
            </router-link>
          </li>

          <!-- Polls - admins manage community polls -->
          <li v-if="isAdmin">
            <router-link
              to="/polls"
              :class="
                getNavLinkClass(['Polls', 'AddPoll', 'EditPoll', 'PollResults'])
              "
              :title="!(isExpanded || isMobileMenuOpen) ? 'Polls' : ''"
            >
              <svg
                :class="[
                  'w-5 h-5',
                  isExpanded || isMobileMenuOpen ? 'mr-3' : '',
                ]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span v-if="isExpanded || isMobileMenuOpen">Polls</span>
            </router-link>
          </li>

          <!-- My Polls - vote / view results (admins who own a unit and residents) -->
          <li>
            <router-link
              to="/my-polls"
              :class="getNavLinkClass('MyPolls')"
              :title="!(isExpanded || isMobileMenuOpen) ? 'My Polls' : ''"
            >
              <svg
                :class="[
                  'w-5 h-5',
                  isExpanded || isMobileMenuOpen ? 'mr-3' : '',
                ]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                />
              </svg>
              <span v-if="isExpanded || isMobileMenuOpen">My Polls</span>
            </router-link>
          </li>

          <li>
            <router-link
              to="/settings"
              :class="getNavLinkClass('Settings')"
              :title="!(isExpanded || isMobileMenuOpen) ? 'Settings' : ''"
            >
              <svg
                :class="[
                  'w-5 h-5',
                  isExpanded || isMobileMenuOpen ? 'mr-3' : '',
                ]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                />
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              <span v-if="isExpanded || isMobileMenuOpen">Settings</span>
            </router-link>
          </li>
        </ul>
      </nav>

      <!-- User Info & Logout -->
      <div
        :class="[
          'border-t border-gray-200',
          isExpanded || isMobileMenuOpen ? 'p-4' : 'p-3',
        ]"
      >
        <div
          v-if="isExpanded || isMobileMenuOpen"
          class="flex items-center space-x-3 mb-4"
        >
          <div
            class="w-8 h-8 bg-primary rounded-full flex items-center justify-center"
          >
            <span class="text-white font-medium text-sm">
              {{ userInitials }}
            </span>
          </div>
          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium text-gray-900 truncate">
              {{ userDisplayName }}
            </p>
            <p class="text-xs text-gray-500 truncate">{{ userEmail }}</p>
          </div>
        </div>
        <div v-else class="flex justify-center mb-4">
          <div
            class="w-8 h-8 bg-primary rounded-full flex items-center justify-center"
            :title="`${userDisplayName} (${userEmail})`"
          >
            <span class="text-white font-medium text-sm">
              {{ userInitials }}
            </span>
          </div>
        </div>

        <button
          @click="handleLogout"
          :disabled="isLoading"
          :class="[
            'flex items-center justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed',
            'w-full',
          ]"
          :title="!(isExpanded || isMobileMenuOpen) ? 'Sign Out' : ''"
        >
          <svg
            v-if="isLoading"
            :class="[
              'animate-spin h-4 w-4 text-gray-500',
              isExpanded || isMobileMenuOpen ? '-ml-1 mr-2' : '',
            ]"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            ></circle>
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <svg
            v-else
            :class="['w-4 h-4', isExpanded || isMobileMenuOpen ? 'mr-2' : '']"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
            />
          </svg>
          <span v-if="isExpanded || isMobileMenuOpen">{{
            isLoading ? 'Signing out...' : 'Sign Out'
          }}</span>
        </button>
      </div>
    </div>

    <!-- Main Content Area -->
    <div class="flex-1 flex flex-col overflow-hidden bg-neutral-50 md:ml-16">
      <!-- Mobile Top Bar -->
      <header
        class="md:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between"
      >
        <div class="flex items-center space-x-3">
          <!-- Hamburger Menu -->
          <button
            @click="toggleMobileMenu"
            class="p-2 rounded-lg hover:bg-gray-100"
          >
            <svg
              class="w-6 h-6 text-gray-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>

          <!-- Page Title -->
          <h2 class="text-lg font-semibold text-gray-900">{{ pageTitle }}</h2>
        </div>

        <!-- Mobile Action Icons -->
        <div class="flex items-center space-x-2">
          <button class="p-2 rounded-lg hover:bg-gray-100">
            <svg
              class="w-5 h-5 text-gray-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </button>
          <button class="p-2 rounded-lg hover:bg-gray-100">
            <svg
              class="w-5 h-5 text-gray-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
              />
            </svg>
          </button>
          <button v-if="!isResident" class="p-2 rounded-lg hover:bg-gray-100">
            <svg
              class="w-5 h-5 text-gray-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              />
            </svg>
          </button>

          <div
            class="w-9 h-9 rounded-full flex items-center justify-center overflow-hidden bg-primary shrink-0 ml-1"
            :title="communityName"
          >
            <img
              v-if="communityLogoUrl"
              :src="communityLogoUrl"
              alt=""
              class="w-full h-full object-cover"
            />
            <span v-else class="text-white font-medium text-xs">
              {{ communityInitials }}
            </span>
          </div>
        </div>
      </header>

      <!-- Desktop Top Header -->
      <header
        v-if="!isAddEditPage"
        class="hidden md:block bg-white border-b border-gray-200 px-6 py-4"
      >
        <div class="flex items-center justify-between">
          <div>
            <h2 class="text-2xl font-semibold text-gray-900">
              {{ pageTitle }}
            </h2>
            <p class="text-sm text-gray-600 mt-1">{{ pageDescription }}</p>
          </div>

          <!-- Desktop Action Icons -->
          <div class="flex items-center space-x-3">
            <button class="p-2 rounded-lg hover:bg-gray-100 transition-colors">
              <svg
                class="w-5 h-5 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </button>
            <button class="p-2 rounded-lg hover:bg-gray-100 transition-colors">
              <svg
                class="w-5 h-5 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                />
              </svg>
            </button>

            <!-- Create Dropdown -->
            <div v-if="!isResident" class="relative">
              <button
                @click="toggleCreateDropdown"
                class="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <svg
                  class="w-5 h-5 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                  />
                </svg>
              </button>

              <!-- Create Dropdown Menu -->
              <Transition
                enter-active-class="transition ease-out duration-100"
                enter-from-class="transform opacity-0 scale-95"
                enter-to-class="transform opacity-100 scale-100"
                leave-active-class="transition ease-in duration-75"
                leave-from-class="transform opacity-100 scale-100"
                leave-to-class="transform opacity-0 scale-95"
              >
                <div
                  v-if="isCreateDropdownOpen"
                  class="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50"
                  @click="closeCreateDropdown"
                >
                  <router-link to="/units/add" class="dropdown-item">
                    <svg
                      class="w-5 h-5 text-primary mr-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                      />
                    </svg>
                    Unit
                  </router-link>
                  <router-link to="/invoices/create" class="dropdown-item">
                    <svg
                      class="w-5 h-5 text-primary mr-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    HOA Dues
                  </router-link>
                  <router-link to="/people/add" class="dropdown-item">
                    <svg
                      class="w-5 h-5 text-primary mr-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                    Resident
                  </router-link>
                  <router-link to="/expenses/add" class="dropdown-item">
                    <svg
                      class="w-5 h-5 text-primary mr-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
                      />
                    </svg>
                    Expense
                  </router-link>
                  <router-link to="/vendors/add" class="dropdown-item">
                    <svg
                      class="w-5 h-5 text-primary mr-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5"
                      />
                    </svg>
                    Vendor
                  </router-link>
                </div>
              </Transition>
            </div>

            <div
              class="w-11 h-11 rounded-full flex items-center justify-center overflow-hidden bg-primary shrink-0"
              :title="communityName"
            >
              <img
                v-if="communityLogoUrl"
                :src="communityLogoUrl"
                alt=""
                class="w-full h-full object-cover"
              />
              <span v-else class="text-white font-medium text-sm">
                {{ communityInitials }}
              </span>
            </div>
          </div>
        </div>
      </header>

      <!-- Main Content -->
      <main class="flex-1 overflow-auto p-4 md:p-6">
        <slot />
      </main>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '../stores/auth';
import { useSettings, useTenantLogoUrl } from '@neibrpay/api-client';
import NeibrPayLogo from './NeibrPayLogo.vue';

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const { data: settingsData } = useSettings();
const { logoUrl: tenantLogoUrl } = useTenantLogoUrl(settingsData);

// Hover state for sidebar expansion
const isExpanded = ref(false);
const isMobileMenuOpen = ref(false);
const isCreateDropdownOpen = ref(false);

// Handle sidebar hover
const handleSidebarHover = (hovered: boolean) => {
  isExpanded.value = hovered;
};

// Toggle functions
const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value;
};

const closeMobileMenu = () => {
  isMobileMenuOpen.value = false;
};

// Close mobile menu when clicking on navigation links
const handleNavClick = (event: Event) => {
  // Only close if clicking on a router-link (not on the nav container itself)
  const target = event.target as HTMLElement;
  if (target.closest('a') && isMobileMenuOpen.value) {
    closeMobileMenu();
  }
};

const toggleCreateDropdown = () => {
  isCreateDropdownOpen.value = !isCreateDropdownOpen.value;
};

const closeCreateDropdown = () => {
  isCreateDropdownOpen.value = false;
};

// Computed properties
const communityName = computed(() => authStore.tenantName || 'Community');
const communityLogoUrl = computed(() => tenantLogoUrl.value ?? null);
const userDisplayName = computed(() => authStore.userDisplayName || 'User');
const userEmail = computed(() => authStore.user?.email || '');
const isResident = computed(() => authStore.isResident);
const isAdmin = computed(() => authStore.isAdmin);
const userInitials = computed(() => {
  const name = userDisplayName.value;
  return name
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
});
const communityInitials = computed(() => {
  const name = communityName.value;
  return name
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
});
const isLoading = computed(() => authStore.isLoading);

// Check if current page is an add/edit page
const isAddEditPage = computed(() => {
  return [
    'AddResident',
    'EditResident',
    'AddUnit',
    'EditUnit',
    'AddInvoice',
    'EditInvoice',
    'InvoiceDetail',
    'AddCharge',
    'EditCharge',
    'AddVendor',
    'EditVendor',
    'AddExpense',
    'EditExpense',
    'AddAnnouncement',
    'EditAnnouncement',
  ].includes(route.name as string);
});

// Page title and description based on current route
const pageTitle = computed(() => {
  switch (route.name) {
    case 'Dashboard':
      return 'Dashboard';
    case 'Invoices':
      return 'HOA Dues';
    case 'Charges':
      return 'Charges';
    case 'People':
      return 'Residents';
    case 'Units':
      return 'Units';
    case 'Payments':
      return 'Payments';
    case 'Expenses':
      return 'Expenses';
    case 'Vendors':
      return 'Vendors';
    case 'Documents':
      return 'HOA Documents';
    case 'Announcements':
      return 'Announcements';
    case 'Polls':
    case 'AddPoll':
    case 'EditPoll':
    case 'PollResults':
      return 'Polls';
    case 'MyPolls':
      return 'My Polls';
    case 'Settings':
      return 'Settings';
    case 'Transactions':
      return 'Transactions';
    case 'Budget':
      return 'Budget';
    default:
      return 'Dashboard';
  }
});

const pageDescription = computed(() => {
  switch (route.name) {
    case 'Dashboard':
      return 'Overview of your HOA community management';
    case 'HOA Dues':
      return 'Manage community dues and billing';
    case 'Charges':
      return 'Manage standard charges and fees';
    case 'Residents':
      return 'Manage residents and community members';
    case 'Units':
      return 'Manage units and properties';
    case 'Payments':
      return 'Track payments and financial transactions';
    case 'Expenses':
      return 'Manage vendor expenses and invoices';
    case 'Vendors':
      return 'Manage vendors and service providers';
    case 'Documents':
      return 'Manage community documents and control resident visibility';
    case 'Announcements':
      return 'Manage community announcements and communications';
    case 'Polls':
      return 'Ask the community a question and see how the units answer';
    case 'MyPolls':
      return 'Vote on open polls and see past results';
    case 'Settings':
      return 'Configure your community settings';
    case 'Transactions':
      return 'View your community transactions';
    case 'Budget':
      return 'Track income and expense forecasts vs actuals';
    default:
      return 'Overview of your HOA community management';
  }
});

// Get nav link classes
const getNavLinkClass = (routeNames: string | string[]) => {
  const names = Array.isArray(routeNames) ? routeNames : [routeNames];
  const isActive = names.includes(route.name as string);
  const showExpanded = isExpanded.value || isMobileMenuOpen.value;

  return [
    'flex items-center py-2.5 text-sm font-medium rounded-lg transition-colors duration-200',
    isActive ? 'bg-primary text-white' : 'text-gray-700 hover:bg-gray-100',
    showExpanded ? 'px-4' : 'justify-center px-2',
  ];
};

// Handle logout
const handleLogout = async () => {
  try {
    await authStore.logout();
    router.push('/auth');
  } catch (error) {
    console.error('Logout failed:', error);
    router.push('/auth');
  }
};
</script>

<style scoped>
.dropdown-item {
  @apply flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-150;
}
</style>
